import { useEffect, useRef, useState } from "react";
import {
  pageStatus, whereLabel, patientLabel, acceptSeconds, secondsLabel, escalated, targetFor, serviceLabel, formatPhone,
  type Page, type Outcome, type OutcomeKind, type PageMessage, type FeeCode,
} from "@pagelink/shared";
import { api, ApiError, type OnCallEntry } from "./api.ts";
import { Avatar, Banner, Btn, Icon, Note, Sec, StatusPill, divColor } from "./ui.tsx";

type Step = "none" | "decline" | "cancel" | "complete" | "redirect";
const KIND_FOR: Record<Exclude<Step, "none" | "redirect">, OutcomeKind> = { decline: "DECLINE", cancel: "CANCEL", complete: "COMPLETED" };

/* ---------------------------------------------------------------------------
 * One page. The same head a card has, so opening one is continuous; then the
 * facts, the stepper, the callback, and the actions the viewer is allowed.
 * ------------------------------------------------------------------------ */
export function Detail({ page, siblings = [], meId, onBack, onChange, onOpen, starred, onStar }: {
  page: Page; siblings?: Page[]; meId: string; onBack: () => void; onChange: (p: Page) => void; onOpen: (id: string) => void;
  starred: boolean; onStar: () => void;
}) {
  const [step, setStep] = useState<Step>("none");
  const [outcomeId, setOutcomeId] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [shorts, setShorts] = useState<Map<string, string | null>>(new Map());
  const [feeCodes, setFeeCodes] = useState<FeeCode[]>([]);
  const [feeCode, setFeeCode] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState<OnCallEntry[]>([]);
  const [redirectTo, setRedirectTo] = useState<OnCallEntry | null>(null);
  const seenSent = useRef(false);

  const mine = page.fromId === meId;
  const forMe = page.toId === meId;
  const status = pageStatus(page);
  const accepted = page.events.some((e) => e.type === "ACK");

  useEffect(() => {
    api.picklists().then((r) => { setOutcomes(r.outcomes); setFeeCodes(r.feeCodes); setShorts(new Map(r.services.map((s) => [s.name, s.short]))); })
      .catch(() => setOutcomes([]));
  }, []);

  // Mark seen once, on open, for the recipient only.
  useEffect(() => {
    if (!forMe || seenSent.current || status.resolved) return;
    if (page.events.some((e) => e.type === "SEEN")) return;
    seenSent.current = true;
    api.seen(page.id).then((r) => onChange(r.page)).catch(() => {});
  }, [forMe, page.id, page.events, status.resolved, onChange]);

  const run = async (fn: () => Promise<{ page: Page }>) => {
    setBusy(true);
    setError(null);
    try {
      onChange((await fn()).page);
      setStep("none");
      setOutcomeId(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  };

  const begin = (s: Step) => {
    setStep(s); setOutcomeId(null); setRedirectTo(null);
    if (s === "redirect") api.onCall().then((r) => setOnCall(r.services)).catch(() => setOnCall([]));
  };
  const choices = step === "none" || step === "redirect" ? [] : outcomes.filter((o) => o.kind === KIND_FOR[step]);
  // Anyone on call except me and the sender, grouped by service ("ID, OPAT").
  const redirectable = onCall.filter((e) => e.id !== meId && e.id !== page.fromId);
  const svcOf = (e: OnCallEntry) => serviceLabel(e.service, e.sub_service, shorts.get(e.service));
  const redirectServices = Array.from(new Set(redirectable.map(svcOf))).sort();
  const isChosen = (e: OnCallEntry) => redirectTo?.id === e.id && svcOf(redirectTo) === svcOf(e);
  const chosen = outcomes.find((o) => o.id === outcomeId);

  const who = mine ? (page.toName ?? "Unassigned · Locating") : page.fromName;
  const divc = divColor(page.service);
  const secs = acceptSeconds(page);
  const target = targetFor(page.urgency);
  const late = secs != null && secs > target;
  // A team page is cancelled for every member still holding an open copy.
  const openCopies = mine && siblings.length > 1 ? siblings.filter((s) => !pageStatus(s).resolved) : [page];
  const cancelAll = async (outcome: string) => {
    let last: { page: Page } | null = null;
    for (const s of openCopies) last = await api.cancel(s.id, outcome).catch((e) => { if (s.id === page.id) throw e; return last; });
    return last ?? api.page(page.id);
  };
  // Re-page whoever has not picked up yet: one person, or every member of a
  // group page still waiting.
  const waiting = openCopies.filter((s) => s.toId && !s.events.some((e) => e.type === "ACK"));
  const repages = page.events.filter((e) => e.type === "REPAGE");
  const lastRepage = repages[repages.length - 1];
  const repageAll = async () => {
    let last: { page: Page } | null = null;
    for (const s of waiting) last = await api.repage(s.id).catch((e) => { if (s.id === page.id || waiting.length === 1) throw e; return last; });
    return last ?? api.page(page.id);
  };
  const outcomeEvent = page.events.find((e) => ["DECLINE", "CANCEL", "COMPLETED", "REDIRECT"].includes(e.type));

  // Messages live here, not in a child, so they interleave with the events.
  const [messages, setMessages] = useState<PageMessage[] | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let live = true;
    const load = () => api.messages(page.id).then((r) => live && setMessages(r.messages)).catch(() => {});
    void load();
    const t = setInterval(load, 4000);
    return () => { live = false; clearInterval(t); };
  }, [page.id]);
  const timelineSize = (messages?.length ?? 0) + page.events.length;
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [timelineSize]);
  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      const { message } = await api.sendMessage(page.id, body);
      setMessages((xs) => [...(xs ?? []), message]);
      setDraft("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not send the message.");
    } finally {
      setSending(false);
    }
  };

  const svc = page.teamId ? `Group page · ${page.service}` : serviceLabel(page.service, page.subService, page.serviceShort);
  const hm = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  // Whom a tap on the header's phone calls: the sender's callback, or the person paged.
  const dial = forMe ? page.callback : page.toPhone;
  const dialName = forMe ? page.fromName : page.toName;
  // Ticks on my own page, as a messenger shows them.
  const rank = status.rank;
  const ticks = !mine || status.resolved ? null : rank >= 2 ? "✓✓" : status.state === "delivered" ? "✓✓" : "✓";
  // Status changes worth a line in the conversation. Sent, delivered and seen
  // are the ticks on the page itself.
  const NOTE_TYPES = ["ACK", "REPAGE", "REM1", "REM2", "ALERT", "GAP", "GAP_RESOLVED", "DECLINE", "CANCEL", "COMPLETED", "REDIRECT"];
  const timeline = [
    ...page.events.filter((e) => NOTE_TYPES.includes(e.type)).map((e) => ({ kind: "event" as const, at: e.at, e })),
    ...(messages ?? []).map((m) => ({ kind: "msg" as const, at: m.at, m })),
  ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));

  return (
    <div className="chat">
      <header className="chat-head">
        <button className="icon-btn" onClick={onBack} aria-label="Back to Pages"><Icon name="back" size={20} sw={2.2} /></button>
        <Avatar id={(mine ? page.toId : page.fromId) ?? undefined} name={who} size={38} fontSize={15} />
        <div className="chat-title">
          <div className="nm">{who}</div>
          <div className="sub">
            {page.urgency === "STAT" && <span className="crow-stat">STAT </span>}
            {svc} · <span className={`crow-st st-${status.state}`}>{status.label}</span>
          </div>
        </div>
        {dial && (
          <a className="icon-btn call" href={`tel:${dial}`} aria-label={`Call ${dialName ?? ""}, ${formatPhone(dial)}`}>
            <Icon name="phone" size={20} sw={2.1} />
          </a>
        )}
        <button className="icon-btn" aria-pressed={starred} aria-label={starred ? "Remove star" : "Star this page"} onClick={onStar}>
          <Icon name="star" size={20} sw={1.9} fill={starred ? "currentColor" : "none"} />
        </button>
      </header>

      <div className="chat-body" aria-live="polite">
        <div className="day-pill">{new Date(page.sentAt).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</div>

        {/* The page itself is the first message. */}
        <div className={`bubble page-bubble ${mine ? "mine" : "theirs"}${page.urgency === "STAT" ? " stat" : ""}`}>
          <div className="pb-head">
            {page.urgency === "STAT" ? <span className="crow-stat">STAT</span> : <span className="pb-routine">Routine</span>}
            <b>{page.consultType}</b>
          </div>
          {patientLabel(page) && <div className="mono">{patientLabel(page)}</div>}
          {page.question && <div className="pb-text">{page.question}</div>}
          {page.context && <div className="pb-text">{page.context}</div>}
          <div className="pb-meta">{svc} · {page.unitName ? `${whereLabel(page)} · ${page.unitName}` : whereLabel(page)}</div>
          {page.redirectedFrom && (
            <div className="pb-meta">
              Redirected from {page.redirectedFromName ?? "another clinician"}.{" "}
              {mine && <button className="linkish" onClick={() => onOpen(page.redirectedFrom!)}>See the first page</button>}
            </div>
          )}
          <div className="pb-calls">
            {forMe ? (
              <>
                <a href={`tel:${page.callback}`}><Icon name="phone" size={14} sw={2.2} />Call back on <span className="mono nowrap">{formatPhone(page.callback)}</span></a>
                {page.fromPhone && page.fromPhone !== page.callback && (
                  <a href={`tel:${page.fromPhone}`}><Icon name="phone" size={14} sw={2.2} />Their phone <span className="mono nowrap">{formatPhone(page.fromPhone)}</span></a>
                )}
              </>
            ) : (
              <span>They will call you on <span className="mono nowrap">{formatPhone(page.callback)}</span></span>
            )}
          </div>
          <span className="meta">
            {hm(page.sentAt)}
            {ticks && <span className={`ticks${rank >= 2 ? " seen" : ""}`} aria-hidden="true"> {ticks}</span>}
            {mine && !status.resolved && <span className="sr-only">, {status.label}</span>}
          </span>
        </div>

        {mine && siblings.length > 1 && (
          <div className="info-card" aria-label={`${page.service} group`}>
            <Sec>{page.service} · {siblings.filter((x) => x.events.some((e) => e.type === "ACK")).length} of {siblings.length} answered</Sec>
            {[...siblings].sort((a, b) => (a.toName ?? "").localeCompare(b.toName ?? "")).map((x) => (
              <button key={x.id} className={`row1${x.id === page.id ? " here" : ""}`} onClick={() => x.id !== page.id && onOpen(x.id)}
                aria-current={x.id === page.id ? "page" : undefined}>
                <Avatar id={x.toId ?? undefined} name={x.toName ?? "?"} size={24} fontSize={10} />
                <span className="nm">{x.toName ?? "Unassigned"}</span>
                <StatusPill page={x} />
              </button>
            ))}
          </div>
        )}

        {timeline.map((t) => t.kind === "msg" ? (
          <div key={t.m.id} className={`bubble ${t.m.authorId === meId ? "mine" : "theirs"}`}>
            {t.m.body}
            <span className="meta">{t.m.authorId === meId ? "" : `${t.m.authorName} · `}{hm(t.m.at)}</span>
          </div>
        ) : (
          <div key={t.e.id} className={`sys-note${["DECLINE", "CANCEL", "ALERT"].includes(t.e.type) ? " warn" : t.e.type === "ACK" || t.e.type === "COMPLETED" ? " ok" : ""}`}>
            {t.e.label} · {hm(t.e.at)}
            {t.e.type === "ACK" && secs != null && <> · {late ? `outside the ${secondsLabel(target)} target` : `in ${secondsLabel(secs)}`}</>}
            {t.e.type === "REDIRECT" && mine && page.redirectedTo && <> · <button className="linkish" onClick={() => onOpen(page.redirectedTo!)}>Follow it</button></>}
          </div>
        ))}
        {escalated(page) && !accepted && !status.resolved && <div className="sys-note warn">In escalation</div>}

        {step === "redirect" && (
          <div className="info-card">
            <Sec>Redirect to · the sender is told</Sec>
            {redirectServices.length === 0 && <Note>Nobody else is on call right now.</Note>}
            {redirectServices.map((sv) => (
              <div key={sv} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: divColor(sv), margin: "6px 0 4px" }}>{sv}</div>
                {redirectable.filter((e) => svcOf(e) === sv).sort((a, b) => a.position - b.position).map((e) => (
                  <button key={`${sv}-${e.id}`} type="button" className="popt" aria-selected={isChosen(e)}
                    onClick={() => setRedirectTo(e)}>
                    <Avatar id={e.id} name={e.name} size={30} fontSize={13} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="nm">{e.name}</div>
                      <div className="tt"><span>{e.title}</span><span className={`poschip${e.position === 1 ? " first" : ""}`}>{e.position === 1 ? "1st call" : "On call"}</span></div>
                    </div>
                    <span className={`check${isChosen(e) ? " on" : ""}`} aria-hidden="true">
                      {isChosen(e) && <Icon name="check" size={13} sw={3} />}
                    </span>
                  </button>
                ))}
              </div>
            ))}
            <Btn full disabled={!redirectTo || busy}
              onClick={() => redirectTo && void run(() => api.redirect(page.id, { toId: redirectTo.id, service: redirectTo.service, subService: redirectTo.sub_service }))}>
              {redirectTo ? `Redirect to ${redirectTo.name}` : "Choose who to redirect to"}
            </Btn>
            <div className="stack"><Btn full sm soft tone="dim" onClick={() => begin("none")}>Back</Btn></div>
          </div>
        )}

        {step !== "none" && step !== "redirect" && (
          <div className="info-card">
            <Sec>
              {step === "decline" ? "Reason for declining · the sender will see this"
                : step === "cancel" ? "Reason for cancelling · the recipient will see this"
                : "How did it end"}
            </Sec>
            <div className="chips">
              {choices.map((o) => (
                <button key={o.id} type="button" className={`chip sm${step !== "complete" ? " stat" : ""}`} aria-pressed={outcomeId === o.id}
                  onClick={() => setOutcomeId(outcomeId === o.id ? null : o.id)}>{o.label}</button>
              ))}
            </div>
            {step === "complete" && feeCodes.length > 0 && (
              <div className="fsec" style={{ marginTop: 10 }}>
                <label htmlFor="fee">Fee code <span style={{ fontWeight: 400, color: "var(--faint)" }}>(optional, can be set later in Billing)</span></label>
                <select id="fee" value={feeCode} onChange={(e) => setFeeCode(e.target.value)}>
                  <option value="">Not yet</option>
                  {feeCodes.map((f) => <option key={f.code} value={f.code}>{f.code} · {f.label}</option>)}
                </select>
              </div>
            )}
            {step === "complete" ? (
              <Btn full tone="ok" disabled={busy} onClick={() => void run(() => api.complete(page.id, outcomeId, feeCode || null))}>
                {chosen ? `Mark as done · ${chosen.label}` : "Mark as done"}
              </Btn>
            ) : (
              <Btn full tone="stat" disabled={!outcomeId || busy}
                onClick={() => void run(() => step === "decline" ? api.decline(page.id, outcomeId!) : cancelAll(outcomeId!))}>
                {step === "decline" ? "Send decline to sender"
                  : openCopies.length > 1 ? `Cancel for all ${openCopies.length} · notify each` : "Cancel this page · notify recipient"}
              </Btn>
            )}
            <div className="stack"><Btn full sm soft tone="dim" onClick={() => begin("none")}>{step === "cancel" ? "Keep the page" : "Back"}</Btn></div>
          </div>
        )}

        {(forMe && accepted && step !== "complete" && feeCodes.length > 0) || (status.resolved && !page.hidden) || page.hidden ? (
          <div className="info-card">
            {forMe && accepted && step !== "complete" && feeCodes.length > 0 && (
              <div className="sheet-row">
                <span className="k">Billed as</span>
                <select aria-label="Fee code" value={page.feeCode ?? ""} disabled={busy} style={{ maxWidth: "62%" }}
                  onChange={(e) => void run(() => api.setFeeCode(page.id, e.target.value || null))}>
                  <option value="">Not billed yet</option>
                  {feeCodes.map((f) => <option key={f.code} value={f.code}>{f.code} · {f.label}</option>)}
                </select>
              </div>
            )}
            {/* The tap alternative to swiping a row away (WCAG 2.5.7). */}
            {status.resolved && !page.hidden && (
              <div className="sheet-row">
                <span className="k">Finished</span>
                <button className="linkish" disabled={busy}
                  onClick={() => void run(async () => { await api.hide(page.id, "archived"); return api.page(page.id); })}>Move to bin</button>
              </div>
            )}
            {page.hidden && (
              <div className="sheet-row">
                <span className="k">{page.hidden === "archived" ? "In the bin" : "Deleted from your lists"}</span>
                <button className="linkish" disabled={busy}
                  onClick={() => void run(async () => { await api.unhide(page.id); return api.page(page.id); })}>Restore</button>
                {page.hidden === "archived" && (
                  <button className="linkish danger" disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Delete this page from your lists? It is gone from Pages and History for you. The other doctor keeps it, and the hospital record is unchanged.")) return;
                      void run(async () => { await api.hide(page.id, "deleted"); return api.page(page.id); });
                    }}>Delete</button>
                )}
              </div>
            )}
          </div>
        ) : null}
        <div ref={end} />
      </div>

      <div className="chat-foot">
        {error && <Banner kind="err">{error}</Banner>}
        {forMe && lastRepage && !accepted && !status.resolved && (
          <div className="fine" style={{ textAlign: "left", color: "var(--urgent)", fontWeight: 600 }}>
            {page.fromName} re-paged you{repages.length > 1 ? ` ${repages.length} times` : ""}, last at {hm(lastRepage.at)}.
          </div>
        )}
        {step === "none" && !status.resolved && (
          <div className="chat-actions">
            {forMe && !accepted && (<>
              <Btn tone="ok" disabled={busy} onClick={() => void run(() => api.accept(page.id))}><Icon name="phone" size={15} sw={2.4} />Answer</Btn>
              {page.canDecline && <Btn soft disabled={busy} onClick={() => begin("redirect")}><Icon name="users" size={15} sw={2.2} />Redirect</Btn>}
              {page.canDecline && <Btn soft tone="stat" disabled={busy} onClick={() => begin("decline")}><Icon name="x" size={15} sw={2.6} />Decline</Btn>}
            </>)}
            {forMe && accepted && (
              <Btn tone="ok" disabled={busy} onClick={() => begin("complete")}><Icon name="check" size={15} sw={2.6} />Mark as done</Btn>
            )}
            {mine && waiting.length > 0 && (
              <Btn disabled={busy} onClick={() => void run(repageAll)}>
                <Icon name="bell" size={15} sw={2.4} />{waiting.length > 1 ? `Re-page ${waiting.length}` : "Re-page"}
              </Btn>
            )}
            {mine && <Btn soft tone="stat" disabled={busy} onClick={() => begin("cancel")}><Icon name="x" size={14} sw={2.4} />{openCopies.length > 1 ? "Cancel all" : "Cancel"}</Btn>}
          </div>
        )}
        {forMe && !accepted && !status.resolved && step === "none" && (
          <div className="fine" style={{ textAlign: "left" }}>
            {page.canDecline ? "Tap Answer once you have reached them. That stops the reminders." : `A ${page.consultType.toLowerCase()} page cannot be declined or redirected. Tap Answer once you have reached them.`}
          </div>
        )}
        {mine && lastRepage && waiting.length > 0 && step === "none" && (
          <div className="fine" style={{ textAlign: "left" }}>Re-paged {repages.length > 1 ? `${repages.length} times, last ` : ""}at {hm(lastRepage.at)}. Once a minute at most.</div>
        )}
        <div className="composer">
          <textarea value={draft} maxLength={1000} rows={1} aria-label="Write a message" placeholder="Message"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} />
          <Btn disabled={!draft.trim() || sending} onClick={() => void send()} ariaLabel="Send message"><Icon name="send" size={17} sw={2} /></Btn>
        </div>
      </div>
    </div>
  );
}
